// src/components/themes/default/admin/BranchGalleryTab.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import Swal from "sweetalert2";
import {
  Images,
  UploadCloud,
  Trash2,
  Star,
  Eye,
  Filter,
  X,
  Loader2,
  CheckCircle2,
  Search,
  ChevronLeft,
  ChevronRight,
  Package,
} from "lucide-react";

interface BranchGalleryTabProps {
  subdomain: string;
  products: any[];
  branchId?: number;
  branchSlug?: string;
  branchName?: string;
  onRefresh: () => void;
  theme?: "dark" | "light";
  primaryColor?: string;
}

export default function BranchGalleryTab({
  subdomain,
  products = [],
  branchId,
  branchSlug,
  branchName = "Branch Outlet",
  onRefresh,
  theme = "dark",
  primaryColor = "#059669",
}: BranchGalleryTabProps) {
  const isLight = theme === "light";
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ফিল্টার ও সার্চ স্টেট
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<string>("all");
  const [imageTypeFilter, setImageTypeFilter] = useState<"all" | "cover" | "additional">("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "product_name">("newest");

  // পেজিনেশন
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  // আপলোড মোডাল
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProductId, setUploadProductId] = useState<string>("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [previewImage, setPreviewImage] = useState<any | null>(null);

  const getApiBase = useCallback(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1" || host.includes("localhost")) {
        return `http://${subdomain}.localhost:8000`;
      }
      return "";
    }
    return "";
  }, [subdomain]);

  const getBranchToken = useCallback(() => {
    if (typeof window === "undefined") return "";
    try {
      if (branchSlug) {
        const exactKey = `branch_auth_${subdomain}_${branchSlug}`;
        const exactData = JSON.parse(localStorage.getItem(exactKey) || "{}");
        if (exactData?.token) return exactData.token;
      }
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("branch_auth_")) {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          if (data?.token) return data.token;
        }
      }
    } catch {}
    return (
      localStorage.getItem("merchant_token") ||
      localStorage.getItem("token") ||
      ""
    );
  }, [branchSlug, subdomain]);

  const resolveImageUrl = (raw: string | null) => {
    if (!raw) return "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=400&q=80";
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      if (raw.includes("127.0.0.1:8000") || raw.includes("localhost:8000")) {
        const apiBase = getApiBase();
        return raw.replace(/http:\/\/(127\.0\.0\.1|localhost):8000/, apiBase);
      }
      return raw;
    }
    const clean = raw.startsWith("/") ? raw : `/${raw}`;
    const apiBase = getApiBase();
    return `${apiBase}${clean}`;
  };

  // ১. শুধুমাত্র ব্রাঞ্চের প্রোডাক্টের ছবি ফেচ করা
  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const token = getBranchToken().trim();
      const headers: Record<string, string> = {
        Accept: "application/json",
        ...(branchId ? { "X-Branch-ID": String(branchId) } : {}),
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const bParam = branchId ? `&branch_id=${branchId}` : "";
      const res = await fetch(
        `${apiBase}/api/v1/products/images/?page_size=1000&limit=1000&ordering=-id&no_page=true${bParam}`,
        { headers }
      );

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setImages(list);
      }
    } catch (err) {
      console.error("Branch gallery fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [branchId, getApiBase, getBranchToken]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // ২. ব্রাঞ্চ প্রোডাক্টের ছবি আপলোড হ্যান্ডলার
  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadProductId || !uploadFile) {
      setUploadError("দয়া করে প্রোডাক্ট এবং ছবি সিলেক্ট করুন।");
      return;
    }

    setUploadLoading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("image", uploadFile);
      if (isPrimary) {
        formData.append("is_primary", "true");
      }

      const apiBase = getApiBase();
      const res = await fetch(
        `${apiBase}/api/v1/products/${uploadProductId}/upload-image/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getBranchToken().trim()}`,
            Accept: "application/json",
            ...(branchId ? { "X-Branch-ID": String(branchId) } : {}),
          },
          body: formData,
        }
      );

      if (res.ok) {
        setIsUploadModalOpen(false);
        setUploadFile(null);
        setIsPrimary(false);
        fetchImages();
        onRefresh();

        Swal.fire({
          icon: "success",
          title: "আপলোড সফল হয়েছে!",
          text: "প্রোডাক্টের নতুন ছবি যুক্ত করা হয়েছে।",
          toast: true,
          position: "top-end",
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        const data = await res.json().catch(() => ({}));
        setUploadError(data.detail || data.error || "ছবি আপলোড করা সম্ভব হয়নি।");
      }
    } catch {
      setUploadError("সার্ভারের সাথে সংযোগ স্থাপন করা যায়নি।");
    } finally {
      setUploadLoading(false);
    }
  };

  // ৩. কভার ছবি সেট করা
  const handleSetCover = async (img: any) => {
    try {
      const apiBase = getApiBase();
      const res = await fetch(
        `${apiBase}/api/v1/products/${img.product}/images/${img.id}/set-primary/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getBranchToken().trim()}`,
            Accept: "application/json",
            ...(branchId ? { "X-Branch-ID": String(branchId) } : {}),
          },
        }
      );
      if (res.ok) {
        fetchImages();
        onRefresh();
        Swal.fire({
          icon: "success",
          title: "কভার ছবি সেট হয়েছে",
          toast: true,
          position: "top-end",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch {
      alert("কভার ছবি পরিবর্তন করা যায়নি।");
    }
  };

  // ৪. ছবি ডিলিট করা
  const handleDeleteImage = async (img: any) => {
    const result = await Swal.fire({
      title: "ছবিটি ডিলিট করতে চান?",
      text: "এটি প্রোডাক্টের গ্যালারি থেকে স্থায়ীভাবে মুছে যাবে!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      cancelButtonColor: "#64748b",
      confirmButtonText: "হ্যাঁ, ডিলিট করুন",
      cancelButtonText: "বাতিল",
      background: isLight ? "#ffffff" : "#0f172a",
      color: isLight ? "#0f172a" : "#ffffff",
    });

    if (!result.isConfirmed) return;

    try {
      const apiBase = getApiBase();
      const res = await fetch(`${apiBase}/api/v1/products/images/${img.id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getBranchToken().trim()}`,
          Accept: "application/json",
          ...(branchId ? { "X-Branch-ID": String(branchId) } : {}),
        },
      });

      if (res.ok || res.status === 204) {
        fetchImages();
        onRefresh();
        Swal.fire({
          icon: "success",
          title: "ডিলিট সম্পন্ন হয়েছে",
          toast: true,
          position: "top-end",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        alert("ছবিটি ডিলিট করা যায়নি।");
      }
    } catch {
      alert("সার্ভার ত্রুটি।");
    }
  };

  // ফিল্টার্ড ছবি
  const filteredImages = useMemo(() => {
    return images
      .filter((img) => {
        const matchesProduct =
          selectedProductId === "all" ||
          String(img.product) === String(selectedProductId);

        const q = searchQuery.toLowerCase().trim();
        const productName = (img.product_name || "").toLowerCase();
        const altText = (img.alt_text || "").toLowerCase();
        const matchesSearch = q === "" || productName.includes(q) || altText.includes(q);

        let matchesType = true;
        if (imageTypeFilter === "cover") matchesType = Boolean(img.is_primary);
        else if (imageTypeFilter === "additional") matchesType = !img.is_primary;

        return matchesProduct && matchesSearch && matchesType;
      })
      .sort((a, b) => {
        if (sortBy === "oldest") return (a.id || 0) - (b.id || 0);
        if (sortBy === "product_name") {
          return (a.product_name || "").localeCompare(b.product_name || "");
        }
        return (b.id || 0) - (a.id || 0);
      });
  }, [images, selectedProductId, searchQuery, imageTypeFilter, sortBy]);

  const totalPages = Math.ceil(filteredImages.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredImages.length);

  const paginatedImages = useMemo(() => {
    return filteredImages.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredImages, startIndex, itemsPerPage]);

  return (
    <div className="w-full space-y-5 font-sans transition-colors duration-200 animate-in fade-in">
      {/* ১. হেডার ও আপলোড অ্যাকশন */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 rounded-3xl bg-slate-900 border border-slate-800 text-white shadow-xl">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg sm:text-xl font-black flex items-center gap-2.5">
              <Images className="w-5 h-5 text-emerald-400" />
              <span>Branch Media Gallery</span>
            </h2>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono">
              Outlet Mode
            </span>
          </div>
          <p className="text-xs text-slate-400">
            {branchName} • Manage photos, covers, and gallery assets ({images.length} total images)
          </p>
        </div>

        <button
          onClick={() => {
            if (products.length > 0 && !uploadProductId) {
              setUploadProductId(String(products[0].id));
            }
            setIsUploadModalOpen(true);
          }}
          style={{ backgroundColor: primaryColor }}
          className="px-5 py-2.5 rounded-2xl text-white text-xs font-bold transition-all flex items-center gap-2 shadow-lg hover:opacity-90 active:scale-95 cursor-pointer self-start sm:self-auto"
        >
          <UploadCloud className="w-4 h-4" />
          <span>Upload Image</span>
        </button>
      </div>

      {/* ২. ফিল্টার কন্ট্রোল বার */}
      <div
        className={`p-4 rounded-2xl space-y-3 transition-all border ${
          isLight
            ? "bg-white border-slate-200/90 shadow-md shadow-slate-200/50"
            : "bg-slate-900/90 border-slate-800"
        }`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search product image..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl text-xs focus:outline-emerald-500 transition-all border ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white"
                  : "bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
              }`}
            />
          </div>

          <div>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className={`w-full px-3 py-2 rounded-xl text-xs focus:outline-emerald-500 cursor-pointer border ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-800"
                  : "bg-slate-950 border-slate-800 text-white"
              }`}
            >
              <option value="all">All Branch Products ({products.length})</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={imageTypeFilter}
              onChange={(e) => setImageTypeFilter(e.target.value as any)}
              className={`w-full px-3 py-2 rounded-xl text-xs focus:outline-emerald-500 cursor-pointer border ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-800"
                  : "bg-slate-950 border-slate-800 text-white"
              }`}
            >
              <option value="all">All Photo Types</option>
              <option value="cover">Cover / Primary Photos</option>
              <option value="additional">Gallery / Extra Photos</option>
            </select>
          </div>

          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className={`w-full px-3 py-2 rounded-xl text-xs focus:outline-emerald-500 cursor-pointer border ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-800"
                  : "bg-slate-950 border-slate-800 text-white"
              }`}
            >
              <option value="newest">Sort: Newest First</option>
              <option value="oldest">Sort: Oldest First</option>
              <option value="product_name">Sort: Product Name A-Z</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1 overflow-x-auto scrollbar-none text-[11px] font-bold">
          <span className={`flex items-center gap-1 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
            <Filter className="w-3.5 h-3.5 text-emerald-400" /> Filter:
          </span>
          {[
            { id: "all", label: "All Photos" },
            { id: "cover", label: "Primary Covers" },
            { id: "additional", label: "Gallery Extras" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setImageTypeFilter(tab.id as any)}
              className={`px-3 py-1 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                imageTypeFilter === tab.id
                  ? "bg-emerald-600 text-white"
                  : isLight
                  ? "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                  : "bg-slate-950 text-slate-400 hover:text-white border border-slate-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
          <span className="text-slate-400 ml-auto font-mono text-[10px]">
            Showing {filteredImages.length} of {images.length}
          </span>
        </div>
      </div>

      {/* ৩. ইমেজ গ্রিড */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 style={{ color: primaryColor }} className="w-8 h-8 animate-spin" />
        </div>
      ) : filteredImages.length === 0 ? (
        <div
          className={`text-center py-20 border rounded-3xl space-y-2 ${
            isLight ? "bg-white border-slate-200 shadow-sm" : "bg-slate-900/60 border-slate-800"
          }`}
        >
          <Images className="w-10 h-10 mx-auto text-slate-400 opacity-60" />
          <p className={`text-sm font-bold ${isLight ? "text-slate-800" : "text-slate-400"}`}>
            কোনো প্রোডাক্টের ছবি পাওয়া যায়নি।
          </p>
          <span className="text-xs text-slate-400">
            ওপরের "+ Upload Image" বোতামে ক্লিক করে নতুন ছবি যোগ করুন।
          </span>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {paginatedImages.map((img) => {
              const displayUrl = resolveImageUrl(img.image_url || img.image);
              return (
                <div
                  key={img.id}
                  className={`group relative rounded-2xl overflow-hidden shadow-md transition-all border flex flex-col justify-between ${
                    isLight
                      ? "bg-white border-slate-200 hover:border-slate-300"
                      : "bg-slate-900 border-slate-800 hover:border-slate-700"
                  }`}
                >
                  <div className="relative aspect-square w-full overflow-hidden bg-slate-950/20">
                    <Image
                      src={displayUrl}
                      alt={img.alt_text || img.product_name || "Product Image"}
                      fill
                      unoptimized
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    {img.is_primary && (
                      <div className="absolute top-2 left-2 z-10 bg-emerald-500 text-slate-950 font-black text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-md shadow-md flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-slate-950" /> Cover
                      </div>
                    )}

                    {/* ডেস্কটপ হোভার ওভারলে */}
                    <div className="hidden md:flex absolute inset-0 bg-slate-950/75 opacity-0 group-hover:opacity-100 transition-opacity flex-col justify-between p-3 z-10">
                      <div className="flex justify-between items-center">
                        <button
                          onClick={() => setPreviewImage(img)}
                          className="p-1.5 bg-slate-800/80 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors cursor-pointer"
                          title="Zoom View"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => handleDeleteImage(img)}
                          className="p-1.5 bg-rose-600/80 hover:bg-rose-600 text-white rounded-lg transition-colors cursor-pointer"
                          title="Delete Image"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <div>
                        <p className="text-[10px] font-bold text-white truncate">
                          {img.product_name || "Product"}
                        </p>
                        {!img.is_primary && (
                          <button
                            onClick={() => handleSetCover(img)}
                            className="mt-1 w-full py-1 rounded bg-slate-800 hover:bg-emerald-600 text-white text-[9px] font-bold transition-colors cursor-pointer"
                          >
                            Set as Cover
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* মোবাইল অ্যাকশন বার */}
                  <div
                    className={`md:hidden p-2 flex flex-col gap-1.5 border-t ${
                      isLight ? "bg-slate-50 border-slate-100" : "bg-slate-950/60 border-slate-800/60"
                    }`}
                  >
                    <p className={`text-[10px] font-bold truncate ${isLight ? "text-slate-800" : "text-white"}`}>
                      {img.product_name || "Product"}
                    </p>

                    <div className="flex items-center gap-1.5">
                      {!img.is_primary ? (
                        <button
                          onClick={() => handleSetCover(img)}
                          className="flex-1 py-1 px-1.5 rounded-lg bg-emerald-600 active:bg-emerald-700 text-white text-[9px] font-bold transition-colors cursor-pointer text-center"
                        >
                          Set Cover
                        </button>
                      ) : (
                        <span className="flex-1 py-1 text-emerald-500 font-bold text-[9px] text-center">
                          Cover
                        </span>
                      )}

                      <button
                        onClick={() => setPreviewImage(img)}
                        className={`p-1 rounded-lg border ${
                          isLight
                            ? "bg-white border-slate-200 text-slate-600"
                            : "bg-slate-800 border-slate-700 text-slate-300"
                        }`}
                      >
                        <Eye className="w-3 h-3" />
                      </button>

                      <button
                        onClick={() => handleDeleteImage(img)}
                        className="p-1 rounded-lg bg-rose-500/10 text-rose-500 border border-rose-500/20"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* পেজিনেশন */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border rounded-2xl text-xs ${
              isLight
                ? "bg-white border-slate-200/90 shadow-md shadow-slate-200/50"
                : "bg-slate-900/90 border-slate-800"
            }`}
          >
            <div className="flex items-center gap-3 text-slate-400">
              <span>
                Showing <strong className={isLight ? "text-slate-900" : "text-white"}>{startIndex + 1}</strong> to{" "}
                <strong className={isLight ? "text-slate-900" : "text-white"}>{endIndex}</strong> of{" "}
                <strong className={isLight ? "text-slate-900" : "text-white"}>{filteredImages.length}</strong> photos
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                className={`p-1.5 rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer ${
                  isLight
                    ? "bg-white border-slate-200 text-slate-600 hover:bg-slate-100"
                    : "bg-slate-950 border-slate-800 text-slate-300 hover:text-white"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-2 font-bold font-mono text-xs">
                {currentPage} / {totalPages}
              </span>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                className={`p-1.5 rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer ${
                  isLight
                    ? "bg-white border-slate-200 text-slate-600 hover:bg-slate-100"
                    : "bg-slate-950 border-slate-800 text-slate-300 hover:text-white"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* আপলোড ইমেজ মোডাল */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div
            className={`max-w-md w-full border rounded-3xl p-5 sm:p-6 space-y-4 shadow-2xl ${
              isLight ? "bg-white border-slate-200 text-slate-800" : "bg-slate-900 border-slate-800 text-white"
            }`}
          >
            <div className="flex items-center justify-between border-b pb-3 border-slate-800">
              <h3 className={`text-base font-black ${isLight ? "text-slate-900" : "text-white"}`}>
                Upload Branch Product Image
              </h3>
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {uploadError && (
              <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs rounded-xl font-medium">
                {uploadError}
              </div>
            )}

            <form onSubmit={handleUploadSubmit} className="space-y-4 text-xs">
              <div>
                <label className={`font-bold block mb-1 ${isLight ? "text-slate-700" : "text-slate-300"}`}>
                  Select Branch Product *
                </label>
                <select
                  required
                  value={uploadProductId}
                  onChange={(e) => setUploadProductId(e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-xl border focus:outline-emerald-500 cursor-pointer ${
                    isLight
                      ? "bg-slate-50 border-slate-200 text-slate-800"
                      : "bg-slate-950 border-slate-800 text-white"
                  }`}
                >
                  <option value="">Choose a product...</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`font-bold block mb-1 ${isLight ? "text-slate-700" : "text-slate-300"}`}>
                  Select Image File *
                </label>
                <div
                  className={`border border-dashed rounded-2xl p-5 text-center hover:border-emerald-500/40 transition-colors ${
                    isLight ? "border-slate-300 bg-slate-50/50" : "border-slate-800"
                  }`}
                >
                  <input
                    type="file"
                    accept="image/*"
                    required
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="branch-modal-image-file"
                  />
                  <label
                    htmlFor="branch-modal-image-file"
                    className="cursor-pointer flex flex-col items-center gap-1.5"
                  >
                    <UploadCloud className="w-7 h-7 text-emerald-500" />
                    <span className={`text-xs font-bold truncate max-w-[200px] ${isLight ? "text-slate-700" : "text-slate-300"}`}>
                      {uploadFile ? uploadFile.name : "Click to browse image"}
                    </span>
                    <span className="text-[10px] text-slate-400">PNG, JPG, WEBP up to 5MB</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="primary-cover-toggle"
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  className="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                />
                <label
                  htmlFor="primary-cover-toggle"
                  className={`text-xs font-semibold cursor-pointer ${isLight ? "text-slate-700" : "text-slate-300"}`}
                >
                  Set as primary cover photo
                </label>
              </div>

              <div className="flex gap-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(false)}
                  className={`w-1/2 py-2.5 rounded-full border font-bold cursor-pointer ${
                    isLight ? "bg-slate-100 border-slate-200 text-slate-700" : "border-slate-800 text-slate-400"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadLoading || !uploadFile}
                  style={{ backgroundColor: primaryColor }}
                  className="w-1/2 py-2.5 rounded-full text-white font-bold flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 shadow-md"
                >
                  {uploadLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  <span>Upload Now</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* প্রিভিউ মোডাল */}
      {previewImage && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div
            className={`max-w-xl w-full border rounded-3xl p-5 space-y-4 shadow-2xl relative ${
              isLight ? "bg-white border-slate-200 text-slate-800" : "bg-slate-900 border-slate-800 text-white"
            }`}
          >
            <div className="flex items-center justify-between border-b pb-3 border-slate-800">
              <h3 className={`text-sm font-black truncate max-w-[240px] ${isLight ? "text-slate-900" : "text-white"}`}>
                {previewImage.product_name || "Image Preview"}
              </h3>
              <button
                onClick={() => setPreviewImage(null)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              className={`relative w-full h-64 sm:h-80 rounded-2xl overflow-hidden border ${
                isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
              }`}
            >
              <Image
                src={resolveImageUrl(previewImage.image_url || previewImage.image)}
                alt="Preview"
                fill
                unoptimized
                className="object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}